import { string, number, array, bool, func, object, node, shape, oneOfType, arrayOf, oneOf } from 'prop-types';
import { MOCK_UP_LAYERS } from '../components/mockups/constants';
import { VARIATIONS_DISPLAY_MODES } from '../constants';

export const TARGET_TYPES = {
    SELF: '_self',
    BLANK: '_blank',
    PARENT: '_parent',
    TOP: '_top',
};

export const DEFAULT_TARGET_TYPE = TARGET_TYPES.BLANK;

const TARGET_TYPE = oneOf(Object.values(TARGET_TYPES));

export const CHILDREN_TYPE = oneOfType([func, node, arrayOf(node)]);

export const LAYER = {
    tag: string,
    children: string,
};

export const SELLER = {
    username: string,
    thumbnail: string,
    profileUrl: string,
};

export const ROOT = {
    tag: string,
    viewBox: string,
    style: object,
    onUpdate: func,
    layers: arrayOf(shape(LAYER)),
};

export const TEXT_ROOT = {
    ...ROOT,
    layerRole: string,
    onRenderTick: func,
};

export const VARIATION = {
    id: string,
    roots: arrayOf(shape(ROOT)),
    mode: oneOf(Object.values(VARIATIONS_DISPLAY_MODES)),
    previewUrl: string,
    observeChanges: bool,
    observeRootsChanges: bool,
    onPreviewLoad: func,
    dataVariationId: string,
    onTextFieldsRenderTick: func,
    onClick: func,
    scale: number,
};

export const SELLER_VARIATION = {
    ...VARIATION,
    seller: SELLER,
};

export const SUGGESTED_VARIATION = {
    id: string,
    selected: bool,
    onSelect: func,
    className: string,
    ...VARIATION,
};

const VariationShape = shape({
    id: string,
    size: number,
    roots: arrayOf(shape(ROOT)),
    previewUrl: string,
});

export const VARIATION_LISTINGS = {
    variations: arrayOf(VariationShape),
    renderChild: func.isRequired,
    wrapperClass: string,
    observeChanges: bool,
    mode: string,
    loadMore: func,
};

export const SUGGESTED_VARIATION_LISTINGS = {
    variations: arrayOf(VariationShape),
    selectedSuggestionsIds: array,
    onSuggestionChange: func,
};

export const DRAFT_VARIATION_CARD = {
    draftId: string,
    projectId: string,
    onCustomize: func,
    onDelete: func,
    onDuplicate: func,
    onBuyAndDownload: func,
    variation: shape(VARIATION),
    isDeleteDisabled: bool,
    isDuplicateDisabled: bool,
    seller: shape(SELLER),
    href: string,
};

export const ORDER_VARIATION_CARD = {
    orderId: string,
    encryptedOrderId: string,
    username: string,
    onDuplicate: func,
    isDuplicateDisabled: bool,
    downloadLink: string,
    purchaseDate: string,
    packageType: string,
    variation: shape(VARIATION),
    isSellerProjectDeleted: bool,
    deliveryPreviewUrl: string,
    backgroundColor: string,
    onDownload: func,
    clickable: bool,
};

export const LOGO_USER_SAVED = {
    id: string,
    project_id: string,
    variation_id: string,
    brandName: string,
    slogan: string,
};

export const LOGO_PROJECT_CARD = {
    variations: arrayOf(VariationShape),
    id: string,
    isMobile: bool,
    isTablet: bool,
    userSaved: shape(LOGO_USER_SAVED),
    brandName: string,
    slogan: string,
    activeVariationIndex: number,
    navigationParams: object,
    showUserSaved: bool,
    onUserSavingChange: func,
    onPreviewLoad: func,
    onLogoProjectCardClick: func,
    onLogoProjectArrowClick: func,
    impressionEnrichment: object,
    target: TARGET_TYPE,
    seller: object,
    withMetadataCard: bool,
    withArrowSlider: bool,
    setTriggerRef: func,
};

export const VARIATIONS_PREVIEW_CARD = {
    variations: arrayOf(VariationShape),
    target: TARGET_TYPE,
    leftoverCounter: number,
    variationsURL: string,
    onClick: func,
    activeIndex: number,
    displayIndex: number,
    onMouseEnter: func,
    onMouseLeave: func,
};

export const INDUSTRY_PROJECT_CARD = {
    seller: SELLER,
    variation: VARIATION,
    brandName: string,
    slogan: string,
    backgroundColor: string,
    isLoading: bool,
};

export const LOGO_PROJECT_LISTINGS = {
    projects: arrayOf(shape(LOGO_PROJECT_CARD)),
    userSavings: arrayOf(shape(LOGO_USER_SAVED)),
    loadMore: func,
    isMobile: bool,
    isTablet: bool,
    brandName: string,
    slogan: string,
    showUserSavings: bool,
    navigationParams: object,
    onUserSavingsChange: func,
    onPreviewLoad: func,
    onLogoProjectCardClick: func,
    onLogoProjectArrowClick: func,
    onLoadMoreVariationsClick: func,
    impressionEnrichment: object,
    target: TARGET_TYPE,
};

const MOCK_UP_LAYER = {
    mixBlendMode: string,
    opacity: number,
};

export const MOCK_UP_BACKGROUND_LAYER = {
    background: string,
    zIndex: number,
    className: string,
    ...MOCK_UP_LAYER,
};

export const MOCK_UP_IMAGE_LAYER = {
    src: string,
    zIndex: number,
    visible: bool,
    ...MOCK_UP_LAYER,
};

export const MOCK_UP_VARIATION_LAYER = {
    transform: string,
    style: object,
    zIndex: number,
    transparentBackground: bool,
    variation: shape(VARIATION).isRequired,
    visible: bool,
    ...MOCK_UP_LAYER,
};

const LAYER_CONFIG = {
    type: oneOf(Object.values(MOCK_UP_LAYERS)).isRequired,
    ...MOCK_UP_LAYER,
};

const IMAGE_LAYER_CONFIG = {
    src: string.isRequired,
    ...LAYER_CONFIG,
};

const VARIATION_LAYER_CONFIG = {
    transform: string.isRequired,
    ...LAYER_CONFIG,
};

const BACKGROUND_LAYER_CONFIG = {
    background: string,
    ...LAYER_CONFIG,
};

export const MOCK_UP_COMPOSITION = {
    layers: arrayOf(
        oneOfType([shape(IMAGE_LAYER_CONFIG), shape(VARIATION_LAYER_CONFIG), shape(BACKGROUND_LAYER_CONFIG)])
    ).isRequired,
    variation: shape(VARIATION).isRequired,
    transparentBackground: bool,
    aspectRatio: string,
    className: string,
};

export const MOCK_UP = {
    variation: shape(VARIATION).isRequired,
    isSquare: bool,
    className: string,
};

export const SLIDER = {
    slides: arrayOf(object).isRequired,
    initialIndex: number,
    Component: CHILDREN_TYPE,
    onSlideChange: func,
    onSlideClick: func,
    isMobile: bool,
};

export const INDICATOR = {
    activeIndex: number,
    total: number,
    onClick: func,
};
